import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { cx } from "./classNames.js";

export type ToastKind = "success" | "error" | "info";

export interface ToastOptions {
  readonly kind?: ToastKind;
  /** Auto-dismiss after this many ms. 0 keeps it until dismissed. Default 4000. */
  readonly duration?: number;
}

interface ToastRecord {
  readonly id: number;
  readonly message: string;
  readonly kind: ToastKind;
  readonly duration: number;
  /** Drives the enter/leave transition. */
  state: "entering" | "visible" | "leaving";
}

interface ToastApi {
  toast: (message: string, opts?: ToastOptions) => void;
  success: (message: string, opts?: ToastOptions) => void;
  error: (message: string, opts?: ToastOptions) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const KIND_ACCENT: Record<ToastKind, string> = {
  success: "border-l-green-500",
  error: "border-l-red-500",
  info: "border-l-arch-500",
};

const KIND_ICON: Record<ToastKind, string> = {
  success: "M5 13l4 4L19 7",
  error: "M6 18L18 6M6 6l12 12",
  info: "M12 9v4m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z",
};

const KIND_ICON_COLOR: Record<ToastKind, string> = {
  success: "text-green-400",
  error: "text-red-400",
  info: "text-arch-400",
};

/**
 * App-wide transient feedback — DESIGN-SYSTEM.md §7. Wrap the app once with
 * <ToastProvider> and call `useToast()` anywhere for action feedback (copied,
 * saved, committed, …). Toasts stack bottom-right, auto-dismiss, and animate
 * in/out. The live region is polite for success/info, assertive for errors.
 *
 * i18n-agnostic by design: pass the localized close-button label via
 * `dismissLabel` (defaults to English "Dismiss") so the kit stays below any
 * app's i18n runtime.
 */
export function ToastProvider({
  children,
  dismissLabel = "Dismiss",
}: {
  readonly children: ReactNode;
  readonly dismissLabel?: string;
}) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const remove = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      // Flip to leaving so the exit transition plays, then drop after it ends.
      setToasts((cur) => cur.map((t) => (t.id === id ? { ...t, state: "leaving" } : t)));
      const timer = setTimeout(() => remove(id), 200);
      timers.current.set(id, timer);
    },
    [remove],
  );

  const push = useCallback(
    (message: string, opts?: ToastOptions) => {
      const id = nextId.current++;
      const duration = opts?.duration ?? 4000;
      const record: ToastRecord = {
        id,
        message,
        kind: opts?.kind ?? "info",
        duration,
        state: "entering",
      };
      setToasts((cur) => [...cur, record]);
      // Next frame: flip to visible so the CSS transition runs.
      requestAnimationFrame(() =>
        setToasts((cur) => cur.map((t) => (t.id === id ? { ...t, state: "visible" } : t))),
      );
      if (duration > 0) {
        const timer = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, timer);
      }
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      toast: push,
      success: (m, o) => push(m, { ...o, kind: "success" }),
      error: (m, o) => push(m, { ...o, kind: "error" }),
      dismiss,
    }),
    [push, dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => map.forEach((t) => clearTimeout(t));
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-toast flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role={t.kind === "error" ? "alert" : "status"}
            className={cx(
              "pointer-events-auto flex items-start gap-2.5 rounded-lg border border-l-4 border-slate-700 bg-slate-900/95 px-3.5 py-3 shadow-lg backdrop-blur",
              "transition-all duration-200 ease-out",
              KIND_ACCENT[t.kind],
              t.state === "visible"
                ? "translate-y-0 opacity-100"
                : "translate-y-1.5 opacity-0",
            )}
          >
            <svg
              className={cx("mt-0.5 h-4 w-4 shrink-0", KIND_ICON_COLOR[t.kind])}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d={KIND_ICON[t.kind]} />
            </svg>
            <p className="flex-1 text-sm text-slate-100">{t.message}</p>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              className="-mr-1 -mt-0.5 rounded p-0.5 text-slate-500 transition-colors hover:text-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
              aria-label={dismissLabel}
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

/** Null-safe variant: returns the toast api, or `null` when no <ToastProvider>
 *  is mounted. For components that MAY render outside a provider (e.g. isolated
 *  in a test harness) and only want feedback opportunistically — call
 *  `useToastOptional()?.error(...)`. */
export function useToastOptional(): ToastApi | null {
  return useContext(ToastContext);
}
